import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { adminTutorialVideoApi } from '../../api/adminTutorialVideo.api';
import { apiHandler, unwrapData } from '../../utils/apiHandler';
import { resolveAssetUrl } from '../../utils/assetUrl';
import { copyToClipboard } from '../subscription-plans/planStripeStatus';
import '../subscription-plans/subscription-plans.css';
import './tutorial-videos.css';

const MEDIA_LABELS = {
  pdf: 'PDF',
  video: 'Video',
  document: 'Document',
};

export default function TutorialVideoList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    const result = await apiHandler(() => adminTutorialVideoApi.list());
    if (result.success) {
      setRows(unwrapData(result) || []);
    } else {
      setError(result.message || 'Failed to load tutorial videos');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const handleDeactivate = async (row) => {
    if (!window.confirm(`Deactivate "${row.title}"?`)) return;
    const result = await apiHandler(() => adminTutorialVideoApi.deactivate(row.id));
    if (result.success) {
      setMessage(result.message || `Deactivated ${row.title}`);
      load();
    } else {
      setError(result.message || 'Failed to deactivate tutorial video');
    }
  };

  const handleCopyLink = async (row) => {
    if (!row.fileUrl) {
      setError('No file uploaded for this tutorial');
      return;
    }
    const url = resolveAssetUrl(row.fileUrl);
    const ok = await copyToClipboard(url);
    if (ok) {
      setMessage(`Copied link for "${row.title}"`);
      setError('');
    } else {
      setError('Could not copy link');
    }
  };

  return (
    <div className="sp-page tv-page">
      <PageHeader
        title="Tutorial Videos"
        subtitle="Manage PDF, video, and document tutorials for application users"
        actions={(
          <button type="button" className="sp-btn sp-btn-primary" onClick={() => navigate('/tutorial-videos/new/edit')}>
            <i className="fa-solid fa-plus" /> Add tutorial
          </button>
        )}
      />

      {error && <div className="sp-alert sp-alert-danger">{error}</div>}
      {message && <div className="sp-alert sp-alert-success">{message}</div>}

      {loading && (
        <div className="sp-loading">
          <i className="fa-solid fa-spinner fa-spin" /> Loading tutorials…
        </div>
      )}

      {!loading && rows.length === 0 && (
        <div className="sp-empty">
          <div><i className="fa-solid fa-circle-play" /></div>
          <p>No tutorial videos yet.</p>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="tv-table-wrap">
          <table className="tv-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Slug</th>
                <th>Type</th>
                <th>Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.title}</td>
                  <td><span className="tv-slug">{row.slug}</span></td>
                  <td>
                    <span className="tv-media-badge">
                      {MEDIA_LABELS[row.mediaType] || row.mediaType}
                    </span>
                  </td>
                  <td>{row.displayOrder ?? 0}</td>
                  <td>
                    <span className={`sp-badge ${row.isActive ? 'sp-badge-active' : 'sp-badge-inactive'}`}>
                      {row.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="tv-actions">
                      {row.fileUrl && (
                        <button
                          type="button"
                          className="sp-btn sp-btn-secondary sp-btn-sm"
                          title="Copy public file link"
                          onClick={() => handleCopyLink(row)}
                        >
                          <i className="fa-solid fa-link" /> Copy link
                        </button>
                      )}
                      <button
                        type="button"
                        className="sp-btn sp-btn-secondary sp-btn-sm"
                        onClick={() => navigate(`/tutorial-videos/${row.id}/edit`)}
                      >
                        Edit
                      </button>
                      {row.isActive && (
                        <button
                          type="button"
                          className="sp-btn sp-btn-secondary sp-btn-sm"
                          onClick={() => handleDeactivate(row)}
                        >
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
